import { jwtDecode } from 'jwt-decode';
import { DEFAULT_COOKIE_OPTIONS, REDIRECT_URL_KEY } from './constants';
import { RoutesPaths } from 'routes/RoutesPathsEnum';

export const openInNewTabHelper = (url: string): void => {
	const deviceInfo = isMobileDevice();

	let target = '_blank';

	if (deviceInfo.isMobile || deviceInfo.isSafari) {
		target = '_self';
	}

	const newWindow = window.open(url, target);

	if (newWindow) newWindow.opener = null;
};

export const countdownHelper = (
	initialCountdown: number,
	onTick: (remainingTime: number) => void,
	onComplete?: () => void
): void => {
	let remainingTime = initialCountdown;

	const interval = setInterval(() => {
		remainingTime -= 1;
		onTick(remainingTime);

		if (remainingTime <= 0) {
			clearInterval(interval);
			if (onComplete) {
				onComplete();
			}
		}
	}, 1000);
};

export const isTokenValid = (token: string): boolean => {
	try {
		const decoded = jwtDecode(token);
		return decoded.exp ? decoded.exp > Date.now() / 1000 : false;
	} catch {
		return false;
	}
};

export const setCookie = (
	name: string,
	value: string,
	days: number = DEFAULT_COOKIE_OPTIONS.days
): void => {
	const date = new Date();
	date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
	const expires = `expires=${date.toUTCString()}`;
	document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=${DEFAULT_COOKIE_OPTIONS.path};secure;samesite=${DEFAULT_COOKIE_OPTIONS.sameSite};domain=${DEFAULT_COOKIE_OPTIONS.domain}`;
};

export const calculateDiscountedPrice = (
	originalPrice: number,
	discountPercentage?: number | null
): number => {
	if (!discountPercentage) {
		return originalPrice;
	}
	const discount = originalPrice * (discountPercentage / 100);
	return parseFloat((originalPrice - discount).toFixed(2));
};

export const handleSuccessfulLogin = () => {
	const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY);

	if (redirectUrl) {
		return redirectUrl;
	}

	return RoutesPaths.REPORTS;
};

export const isMobileDevice = () => {
	const userAgent = navigator.userAgent || navigator.vendor;

	const mobileRegex = {
		android: /android/i,
		ios: /iPad|iPhone|iPod/i,
		windows: /IEMobile|Windows Phone/i,
		opera: /Opera Mini/i,
		blackberry: /BlackBerry/i,
		webOS: /webOS/i,
		any: /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|Windows Phone/i
	};

	const browserRegex = {
		isSafari: /^((?!chrome|android).)*safari/i.test(userAgent),
		isChrome: /chrome/i.test(userAgent),
		isFirefox: /firefox/i.test(userAgent),
		isEdge: /edg/i.test(userAgent)
	};

	const isSmallScreen = window.innerWidth < 768;

	const hasTouchScreen =
		'ontouchstart' in window ||
		navigator.maxTouchPoints > 0 ||
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		navigator.msMaxTouchPoints > 0;

	return {
		isMobile: mobileRegex.any.test(userAgent),
		isAndroid: mobileRegex.android.test(userAgent),
		isIOS: mobileRegex.ios.test(userAgent),
		isWindowsPhone: mobileRegex.windows.test(userAgent),
		isOperaMini: mobileRegex.opera.test(userAgent),
		isBlackBerry: mobileRegex.blackberry.test(userAgent),
		isWebOS: mobileRegex.webOS.test(userAgent),

		isSafari: browserRegex.isSafari,
		isChrome: browserRegex.isChrome,
		isFirefox: browserRegex.isFirefox,
		isEdge: browserRegex.isEdge,

		isSmallScreen,
		hasTouchScreen,

		userAgent,
		screenWidth: window.innerWidth,
		screenHeight: window.innerHeight,
		devicePixelRatio: window.devicePixelRatio || 1
	};
};

type ColorMapping = {
	[key: string]: {
		accent: string;
		background: string;
		text: string;
	};
};

export const colorClasses: ColorMapping = {
	'#9b41ef': {
		accent: 't-purple',
		background: 't-purple/20',
		text: 'text-t-purple'
	},
	'#f06310': {
		accent: 't-orange',
		background: 't-orange/20',
		text: 'text-t-orange'
	},
	'#2e70ef': {
		accent: 't-blue',
		background: 't-blue/20',
		text: 'text-t-blue'
	},
	'#1bb152': {
		accent: 't-green',
		background: 't-green/20',
		text: 'text-t-green'
	}
};
